import React, { ComponentProps } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { Box, Stack, styled, Tooltip } from '@mui/material'
import { UserBubble } from 'ComponentLibrary/src/molecules/UserBubble/UserBubble'
import { UserPlus } from '@phosphor-icons/react'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons/IconButton/IconButton'
import { DisplayUserPresentation, Typography } from 'ComponentLibrary/src/piazza/text'
import type { UserPresentation } from 'ComponentLibrary/src/common/userPresentations'
import type { UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type UserBubbleListBaseProps = {
    size?: ComponentProps<typeof UserBubble>['size']
    maxNumberOfBubbles?: number
    onClick?: () => void
    tooltip?: string
    ariaLabel?: string
    userLinkCopy?: Partial<UserLinkCopy>
}

type PresentationGroup = { groupName: string; presentations: UserPresentation[] }

export type UserBubbleListProps =
    | (UserBubbleListBaseProps & {
          presentations: UserPresentation[]
          presentationGroups?: never
      })
    | (UserBubbleListBaseProps & {
          presentations?: never
          presentationGroups: PresentationGroup[]
      })

export const UserBubbleList = ({
    presentations,
    presentationGroups,
    onClick,
    size = tokens.SizingIconM,
    maxNumberOfBubbles = 3,
    tooltip,
    ariaLabel,
    userLinkCopy,
}: UserBubbleListProps) => {
    // Preserve caller order after deduplication; callers intentionally construct ranked assignments and mixed participant groups.
    const uniquePresentations = presentations
        ? deduplicateUserPresentations(presentations)
        : deduplicateUserPresentations(presentationGroups?.flatMap((group) => group.presentations) ?? [])

    if (uniquePresentations.length === 0) {
        if (onClick) {
            return (
                <Tooltip title={tooltip}>
                    <Box>
                        <IconButton variant="tertiary" size="medium" icon={<UserPlus />} onClick={onClick} />
                    </Box>
                </Tooltip>
            )
        }
        return null
    }

    return (
        <Tooltip
            placement="bottom-end"
            slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [-8, -4] } }] } }}
            componentsProps={{
                tooltip: {
                    sx: {
                        ...tokens.Popup,
                        backgroundColor: tokens.ColorBackgroundLight,
                        minWidth: '232px',
                    },
                },
            }}
            title={
                presentationGroups ? (
                    <UsersPreview presentationGroups={presentationGroups} userLinkCopy={userLinkCopy} />
                ) : (
                    <UsersPreview presentations={uniquePresentations} userLinkCopy={userLinkCopy} />
                )
            }
        >
            <UserBubblesContainer
                $clickable={!!onClick}
                onClick={onClick}
                role={ariaLabel ? 'group' : undefined}
                aria-label={ariaLabel}
                tabIndex={ariaLabel ? 0 : undefined}
            >
                <Stack direction="row" marginLeft={tokens.SizingSpacing100}>
                    {uniquePresentations.slice(0, maxNumberOfBubbles).map((presentation, index) => {
                        const displayData = presentation.kind === 'unavailable' ? undefined : presentation.displayData
                        const isUnavailable = presentation.kind === 'unavailable'
                        return (
                            <Box key={presentation.key} marginLeft={`-${tokens.SizingSpacing100}`} zIndex={maxNumberOfBubbles - index}>
                                <UserBubble
                                    profileImageUrl={displayData?.profile_thumbnail_image_url}
                                    profileFallbackImageUrl={displayData?.profile_image_url}
                                    size={size}
                                    defaultIconVariant={isUnavailable ? 'unavailable' : 'default'}
                                    defaultIconCustomColors={
                                        isUnavailable
                                            ? { color: tokens.ColorIconDisabled, bgColor: tokens.ColorSurfaceDisabled }
                                            : undefined
                                    }
                                />
                            </Box>
                        )
                    })}
                </Stack>
                {uniquePresentations.length > maxNumberOfBubbles && (
                    <Typography variant="textM" color={tokens.ColorTextDefault}>
                        +{uniquePresentations.length - maxNumberOfBubbles}
                    </Typography>
                )}
            </UserBubblesContainer>
        </Tooltip>
    )
}

export function deduplicateUserPresentations(presentations: UserPresentation[]): UserPresentation[] {
    return presentations.filter(createFirstOccurrenceFilter())
}

function createFirstOccurrenceFilter() {
    const seenKeys = new Set<string>()
    return (presentation: UserPresentation) => {
        if (seenKeys.has(presentation.key)) {
            return false
        }
        seenKeys.add(presentation.key)
        return true
    }
}

const UserBubblesContainer = styled(Stack, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})(({ $clickable }: { $clickable: boolean }) => ({
    isolation: 'isolate',
    flexDirection: 'row',
    gap: tokens.SizingSpacing050,
    alignItems: 'center',
    borderRadius: tokens.SizingCornerS,
    cursor: $clickable ? 'pointer' : 'initial',
    padding: $clickable ? tokens.SizingSpacing100 : 'initial',
    '&:hover': {
        backgroundColor: $clickable ? tokens.ColorSurfaceHover : 'initial',
    },
    '&:active': {
        backgroundColor: $clickable ? tokens.ColorSurfacePressed : 'initial',
    },
}))

type UsersPreviewProps =
    | { presentationGroups: PresentationGroup[]; presentations?: never; userLinkCopy?: Partial<UserLinkCopy> }
    | { presentationGroups?: never; presentations: UserPresentation[]; userLinkCopy?: Partial<UserLinkCopy> }

export const UsersPreview = ({ presentationGroups, presentations, userLinkCopy }: UsersPreviewProps) => {
    // Grouped previews preserve duplicate entries because one person may belong to multiple groups.
    // Ungrouped previews receive the deduplicated list to avoid showing the same person repeatedly.
    return (
        <Stack direction="column" gap={tokens.SizingSpacing150} padding={tokens.SizingSpacing075} sx={{ cursor: 'default' }}>
            {presentationGroups
                ? presentationGroups.map(({ groupName, presentations }) => (
                      <React.Fragment key={groupName}>
                          {presentations.length !== 0 && (
                              <Typography variant="headline5" color={tokens.ColorTextSubtle}>
                                  {groupName}
                              </Typography>
                          )}
                          {presentations.map((presentation) => (
                              <DisplayUserPresentation
                                  key={presentation.key}
                                  presentation={presentation}
                                  size="medium"
                                  copy={userLinkCopy}
                              />
                          ))}
                      </React.Fragment>
                  ))
                : presentations.map((presentation) => (
                      <DisplayUserPresentation key={presentation.key} presentation={presentation} size="medium" copy={userLinkCopy} />
                  ))}
        </Stack>
    )
}
